"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import type { CartItem, User, Purchase } from "./types";
import { mockUser, mockPurchases } from "./mock-data";

interface StoreContextType {
  user: User | null;
  isLoggedIn: boolean;
  cart: CartItem[];
  purchases: Purchase[];
  login: () => void;
  logout: () => void;
  addToCart: (item: CartItem) => void;
  removeFromCart: (promptId: string) => void;
  clearCart: () => void;
  hasPurchased: (promptId: string) => boolean;
  isInCart: (promptId: string) => boolean;
  completePurchase: (items: CartItem[]) => void;
  updateUser: (userData: Partial<User>) => void;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

// LocalStorage 키 상수
const STORAGE_KEYS = {
  USER: "prompt-market-user",
  IS_LOGGED_IN: "prompt-market-is-logged-in",
  CART: "prompt-market-cart",
  PURCHASES: "prompt-market-purchases",
} as const;

// LocalStorage에서 데이터 로드
function loadFromLocalStorage<T>(key: string, defaultValue: T): T {
  if (typeof window === "undefined") return defaultValue;
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

// LocalStorage에 데이터 저장
function saveToLocalStorage<T>(key: string, value: T): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to save to localStorage (${key}):`, error);
  }
}

export function StoreProvider({ children }: { children: ReactNode }) {
  // #region agent log
  fetch('http://127.0.0.1:7243/ingest/b4c36c82-8eca-4dca-882e-ad0f6e7a290b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'lib/store.tsx:StoreProvider',message:'StoreProvider render',data:{isServer:typeof window==='undefined'},timestamp:Date.now(),sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H1'})}).catch(()=>{});
  // #endregion

  // LocalStorage에서 초기 데이터 로드
  const [isLoggedIn, setIsLoggedIn] = useState(() =>
    loadFromLocalStorage(STORAGE_KEYS.IS_LOGGED_IN, false)
  );
  const [user, setUser] = useState<User | null>(() =>
    loadFromLocalStorage(STORAGE_KEYS.USER, null)
  );
  const [cart, setCart] = useState<CartItem[]>(() =>
    loadFromLocalStorage(STORAGE_KEYS.CART, [])
  );
  const [purchases, setPurchases] = useState<Purchase[]>(() =>
    loadFromLocalStorage(STORAGE_KEYS.PURCHASES, [])
  );

  // LocalStorage에 동기화
  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.IS_LOGGED_IN, isLoggedIn);
  }, [isLoggedIn]);

  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.USER, user);
  }, [user]);

  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.CART, cart);
  }, [cart]);

  useEffect(() => {
    saveToLocalStorage(STORAGE_KEYS.PURCHASES, purchases);
  }, [purchases]);

  const login = () => {
    setIsLoggedIn(true);
    const userData = mockUser;
    setUser(userData);
    // 기존 구매 내역이 없으면 mock 데이터로 초기화
    if (purchases.length === 0) {
      setPurchases(mockPurchases);
    }
  };

  const logout = () => {
    setIsLoggedIn(false);
    setUser(null);
    setCart([]);
    // 구매 내역은 유지 (로그아웃해도 구매 내역은 남아있어야 함)
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const addToCart = (item: CartItem) => {
    if (!cart.find((i) => i.promptId === item.promptId)) {
      setCart([...cart, item]);
    }
  };

  const removeFromCart = (promptId: string) => {
    setCart(cart.filter((item) => item.promptId !== promptId));
  };

  const clearCart = () => {
    setCart([]);
  };

  const hasPurchased = (promptId: string) => {
    return purchases.some((p) => p.promptId === promptId);
  };

  const isInCart = (promptId: string) => {
    return cart.some((item) => item.promptId === promptId);
  };

  const completePurchase = (items: CartItem[]) => {
    const newPurchases = items.map((item) => ({
      id: `purchase-${Date.now()}-${item.promptId}`,
      promptId: item.promptId,
      prompt: item.prompt,
      purchaseDate: new Date().toISOString().split("T")[0],
    }));
    setPurchases([...purchases, ...newPurchases]);
    clearCart();
  };

  return (
    <StoreContext.Provider
      value={{
        user,
        isLoggedIn,
        cart,
        purchases,
        login,
        logout,
        addToCart,
        removeFromCart,
        clearCart,
        hasPurchased,
        isInCart,
        completePurchase,
        updateUser,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const context = useContext(StoreContext);
  // #region agent log
  fetch('http://127.0.0.1:7243/ingest/b4c36c82-8eca-4dca-882e-ad0f6e7a290b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'lib/store.tsx:useStore',message:'useStore called',data:{contextDefined:context!==undefined,isServer:typeof window==='undefined',stack:(new Error('useStore stack')).stack?.split('\n').slice(0,6).join('\n')},timestamp:Date.now(),sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H2'})}).catch(()=>{});
  // #endregion
  if (context === undefined) {
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/b4c36c82-8eca-4dca-882e-ad0f6e7a290b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'lib/store.tsx:useStore',message:'useStore missing StoreProvider',data:{isServer:typeof window==='undefined',stack:(new Error('missing StoreProvider')).stack?.split('\n').slice(0,8).join('\n')},timestamp:Date.now(),sessionId:'debug-session',runId:'pre-fix',hypothesisId:'H1'})}).catch(()=>{});
    // #endregion
    throw new Error("useStore must be used within a StoreProvider");
  }
  return context;
}
